import { useContext, useCallback, useEffect, useState } from "react";

import { PATHS } from "../constants/paths";
import {
  getAppStockOrdersCount,
  getAllVoiceNotes,
  getAllVendorCounters,
  getSalesDashboardOrdersCounter,
} from "../services/order";
import { AuthContext } from "../context/AuthContext";
import { PAGES_KEYS } from "../constants/pagesKeys";
import { useAutoReload } from "./useAutoReload";

export const useSideDrawerCounts = () => {
  // get user
  const {
    state: {
      userAccess: { allowedPageList },
    },
  } = useContext(AuthContext);

  // auto reload
  const reducerValue = useAutoReload(5);

  // sideDrawer items counts
  const [sideDrawerItemsCounts, setSideDrawerItemsCounts] = useState();

  // get sales dashboard orders counter
  const fetchSalesDashboardOrdersCounter = useCallback(async () => {
    try {
      const response = await getSalesDashboardOrdersCounter();
      const {
        data: {
          data: { ordersCount },
        },
      } = response;

      setSideDrawerItemsCounts((prevState) => ({
        ...prevState,
        [PATHS.salesDashboard]: ordersCount,
      }));
    } catch (error) {
      console.error(error);
    }
  }, []);

  // get all vendor pages Counts
  const fetchVendorCounts = useCallback(async () => {
    try {
      const response = await getAllVendorCounters();
      setSideDrawerItemsCounts((prevState) => ({
        ...prevState,
        [PATHS.acceptedOrders]:
          response.data.data
            .numberOfAcceptedOrdersForAcceptedOrdersDashboardCount,
        [PATHS.opsNeedSupport]:
          response.data.data.numberOfOrdersForNeedSupportsDashboardCount,
        [PATHS.mechanicNeedSupport]:
          response.data.data.mechanicNeedSupportDashboardCount,
        [PATHS.repriceNeededOrders]:
          response.data.data.pendingRepricingOrdersCount,
        [PATHS.vendorSupportPricing]:
          response.data.data.pendingPricingOrdersCount,
      }));
    } catch (error) {
      console.error(error);
    }
  }, []);

  // Voice Notes orders Count
  const fetchVoiceNotesOrdersCounts = useCallback(async () => {
    try {
      const response = await getAllVoiceNotes();
      setSideDrawerItemsCounts((prevState) => ({
        ...prevState,
        [PATHS.voiceOrders]: response.data.orders.voiceOrdersCount,
      }));
    } catch (err) {
      console.error(err);
    }
  }, []);

  // mechanic app stock orders Count
  const fetchMechanicAppCounts = useCallback(async () => {
    try {
      const response = await getAppStockOrdersCount();

      setSideDrawerItemsCounts((prevState) => ({
        ...prevState,
        [PATHS.mechanicAppStockOrders]: response.data.appStockOrdersCount,
      }));
    } catch (error) {
      console.error(error);
    }
  }, []);

  // request counters for sideDrawer items
  useEffect(() => {
    const isVendorSupportPage = allowedPageList?.some((key) =>
      [
        PAGES_KEYS.vendorSupport,
        PAGES_KEYS.repricingOrders,
        PAGES_KEYS.acceptedOrdersDashboard,
        PAGES_KEYS.opsNeedSupport,
        PAGES_KEYS.mechanicNeedSupport,
      ].includes(key)
    );

    const isAppStockOrdersPage = allowedPageList?.includes(
      PAGES_KEYS.appStockOrders
    );
    const isVoiceNotesOrderPage = allowedPageList?.includes(
      PAGES_KEYS.voiceNoteOrders
    );

    const isSalesUserDashboardPage = allowedPageList?.includes(
      PAGES_KEYS.salesUserDashboard
    );

    if (isVendorSupportPage) {
      fetchVendorCounts();
    }

    if (isAppStockOrdersPage) {
      fetchMechanicAppCounts();
    }
    if (isVoiceNotesOrderPage) {
      fetchVoiceNotesOrdersCounts();
    }

    if (isSalesUserDashboardPage) {
      fetchSalesDashboardOrdersCounter();
    }
  }, [
    fetchVendorCounts,
    fetchMechanicAppCounts,
    fetchVoiceNotesOrdersCounts,
    fetchSalesDashboardOrdersCounter,
    allowedPageList,
    reducerValue,
  ]);

  return sideDrawerItemsCounts;
};
